import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Save, Loader2, Plus, Trash2, GripVertical, FolderOpen, ChevronDown, ChevronUp,
  Italic, ExternalLink, AlertCircle, Image as ImageIcon, Users, Quote, Layers
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import MediaPickerModal from "../../components/MediaPickerModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FIELD = "block w-full bg-white border border-[#D4859B]/20 focus:border-seikat-pink rounded-xl px-3.5 py-2 text-[#1C1A19] placeholder:text-[#A8A29A] font-body text-sm outline-none transition-colors";
const LABEL = "block text-[10px] tracking-[0.2em] uppercase text-[#8A857D] mb-1.5 font-body";

function insertEm(text, ref) {
  if (!ref?.current) return text;
  const ta = ref.current;
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  const selected = text.substring(start, end) || "kata";
  return text.substring(0, start) + `<em>${selected}</em>` + text.substring(end);
}

function SortableStory({ section, idx, expanded, onToggleExpand, onUpdate, onRemove, onPickImage }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const titleIdRef = useRef(null);
  const titleEnRef = useRef(null);

  return (
    <div ref={setNodeRef} style={style} className={`bg-white border ${expanded ? "border-seikat-pink/30 shadow-md" : "border-[#D4859B]/15"} rounded-2xl overflow-hidden transition-all`} data-testid={`story-${section.id}`}>
      <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#FDF8F6] to-white">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-seikat-pink/10 text-[#8A857D] hover:text-seikat-pink">
          <GripVertical size={14} />
        </button>
        <span className="font-heading text-xl text-[#1C1A19]">{String(idx + 1).padStart(2, "0")}</span>
        <div className="flex-1 min-w-0">
          <p className="font-body text-sm text-[#1C1A19] truncate">{section.title_id || section.title_en || "Section"}</p>
          <p className="text-[10px] text-[#8A857D] truncate">{section.body_id?.substring(0, 80) || "—"}…</p>
        </div>
        {section.image_url && (
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#FDF8F6] flex-shrink-0">
            <img src={section.image_url} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <button onClick={() => onRemove(section.id)} className="w-8 h-8 rounded-full hover:bg-red-50 hover:text-red-500 text-[#A8A29A] flex items-center justify-center" data-testid={`story-remove-${section.id}`}>
          <Trash2 size={13} />
        </button>
        <button onClick={() => onToggleExpand(section.id)} className="w-8 h-8 rounded-full bg-[#FDF8F6] hover:bg-seikat-pink/10 hover:text-seikat-pink text-[#4A453F] flex items-center justify-center">
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <div className="p-5 border-t border-[#D4859B]/10 grid lg:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={LABEL + " !mb-0"}>🇮🇩 Title — Indonesia</label>
                  <button type="button" onClick={() => onUpdate(section.id, "title_id", insertEm(section.title_id || "", titleIdRef))} className="text-[10px] text-seikat-pink hover:underline flex items-center gap-1"><Italic size={9} /> +italic</button>
                </div>
                <input ref={titleIdRef} className={FIELD} value={section.title_id || ""} onChange={e => onUpdate(section.id, "title_id", e.target.value)} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={LABEL + " !mb-0"}>🇬🇧 Title — English</label>
                  <button type="button" onClick={() => onUpdate(section.id, "title_en", insertEm(section.title_en || "", titleEnRef))} className="text-[10px] text-seikat-pink hover:underline flex items-center gap-1"><Italic size={9} /> +italic</button>
                </div>
                <input ref={titleEnRef} className={FIELD} value={section.title_en || ""} onChange={e => onUpdate(section.id, "title_en", e.target.value)} />
              </div>
              <div><label className={LABEL}>🇮🇩 Body — Indonesia</label><textarea rows={4} className={FIELD} value={section.body_id || ""} onChange={e => onUpdate(section.id, "body_id", e.target.value)} /></div>
              <div><label className={LABEL}>🇬🇧 Body — English</label><textarea rows={4} className={FIELD} value={section.body_en || ""} onChange={e => onUpdate(section.id, "body_en", e.target.value)} /></div>
              <div className="lg:col-span-2">
                <label className={LABEL}>Image URL</label>
                <div className="flex gap-2">
                  <input className={`${FIELD} flex-1`} value={section.image_url || ""} onChange={e => onUpdate(section.id, "image_url", e.target.value)} placeholder="https://…" />
                  <button type="button" onClick={() => onPickImage(section.id)} className="flex items-center gap-1.5 px-3.5 py-2 bg-seikat-pink/10 hover:bg-seikat-pink/20 text-seikat-pink rounded-xl text-xs font-body whitespace-nowrap" data-testid={`story-picker-${section.id}`}>
                    <FolderOpen size={12} /> Galeri
                  </button>
                </div>
                {section.image_url && <img src={section.image_url} alt="" className="mt-2 w-full max-h-56 object-cover rounded-lg" />}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SortableTeam({ member, idx, onUpdate, onRemove, onPickImage }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: member._id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="bg-white border border-[#D4859B]/15 rounded-2xl overflow-hidden" data-testid={`team-${member._id}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#FDF8F6] to-white border-b border-[#D4859B]/10">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-seikat-pink/10 text-[#8A857D] hover:text-seikat-pink"><GripVertical size={13} /></button>
        <span className="font-heading text-sm text-[#1C1A19]">{String(idx + 1).padStart(2, "0")}</span>
        <span className="font-body text-xs text-[#4A453F] flex-1 truncate">{member.name || "Anggota baru"}</span>
        <button onClick={() => onRemove(member._id)} className="w-7 h-7 rounded-full hover:bg-red-50 hover:text-red-500 text-[#A8A29A] flex items-center justify-center"><Trash2 size={12} /></button>
      </div>
      <div className="p-3 space-y-2">
        {member.image_url && <img src={member.image_url} alt="" className="w-full aspect-[4/5] object-cover rounded-lg" />}
        <div><label className={LABEL}>Nama</label><input className={FIELD} value={member.name || ""} onChange={e => onUpdate(member._id, "name", e.target.value)} /></div>
        <div><label className={LABEL}>🇮🇩 Role — ID</label><input className={FIELD} value={member.role_id || ""} onChange={e => onUpdate(member._id, "role_id", e.target.value)} /></div>
        <div><label className={LABEL}>🇬🇧 Role — EN</label><input className={FIELD} value={member.role_en || ""} onChange={e => onUpdate(member._id, "role_en", e.target.value)} /></div>
        <div>
          <label className={LABEL}>Image URL</label>
          <div className="flex gap-2">
            <input className={`${FIELD} flex-1`} value={member.image_url || ""} onChange={e => onUpdate(member._id, "image_url", e.target.value)} />
            <button type="button" onClick={() => onPickImage(member._id)} className="px-3 py-2 bg-seikat-pink/10 hover:bg-seikat-pink/20 text-seikat-pink rounded-xl text-xs font-body" data-testid={`team-picker-${member._id}`}>
              <FolderOpen size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminAbout() {
  const [data, setData] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(null); // { kind, refId, field }
  const [expanded, setExpanded] = useState(new Set());
  const heroIdRef = useRef(null);
  const heroEnRef = useRef(null);
  const quoteIdRef = useRef(null);
  const quoteEnRef = useRef(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    axios.get(`${API}/cms/about-v2`).then(r => {
      // Ensure sections + team have stable IDs
      const d = r.data;
      d.sections = (d.sections || []).map((s, i) => ({ ...s, id: s.id || `sec_${i}_${Date.now()}` }));
      d.team = (d.team || []).map((m, i) => ({ ...m, _id: m._id || `mem_${i}_${Date.now()}` }));
      setData(d);
      setInitial(JSON.stringify(d));
    }).catch(() => toast.error("Gagal memuat about"));
  }, []);

  const isDirty = useMemo(() => data && initial && JSON.stringify(data) !== initial, [data, initial]);

  const updateField = (path, value) => {
    const parts = path.split(".");
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) { cur[parts[i]] = cur[parts[i]] || {}; cur = cur[parts[i]]; }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  // Story sections
  const updateSection = (id, k, v) => setData(prev => ({ ...prev, sections: prev.sections.map(s => s.id === id ? { ...s, [k]: v } : s) }));
  const removeSection = (id) => {
    if (!window.confirm("Hapus section ini?")) return;
    setData(prev => ({ ...prev, sections: prev.sections.filter(s => s.id !== id) }));
  };
  const addSection = () => {
    const id = `sec_${Date.now().toString(36)}`;
    setData(prev => ({
      ...prev,
      sections: [...(prev.sections || []), { id, title_id: "Judul Baru", title_en: "New Title", body_id: "", body_en: "", image_url: "" }],
    }));
    setExpanded(prev => new Set([...prev, id]));
  };
  const handleSectionDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setData(prev => {
      const oldIdx = prev.sections.findIndex(s => s.id === active.id);
      const newIdx = prev.sections.findIndex(s => s.id === over.id);
      return { ...prev, sections: arrayMove(prev.sections, oldIdx, newIdx) };
    });
  };

  // Team
  const updateMember = (id, k, v) => setData(prev => ({ ...prev, team: prev.team.map(m => m._id === id ? { ...m, [k]: v } : m) }));
  const removeMember = (id) => setData(prev => ({ ...prev, team: prev.team.filter(m => m._id !== id) }));
  const addMember = () => {
    const _id = `mem_${Date.now().toString(36)}`;
    setData(prev => ({ ...prev, team: [...(prev.team || []), { _id, name: "New Member", role_id: "Role", role_en: "Role", image_url: "" }] }));
  };
  const handleTeamDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setData(prev => {
      const oldIdx = prev.team.findIndex(m => m._id === active.id);
      const newIdx = prev.team.findIndex(m => m._id === over.id);
      return { ...prev, team: arrayMove(prev.team, oldIdx, newIdx) };
    });
  };

  const toggleExpand = (id) => setExpanded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const save = async () => {
    setSaving(true);
    try {
      const payload = JSON.parse(JSON.stringify(data));
      // Clean team _id (keep for client, server can ignore)
      await axios.put(`${API}/admin/cms/about`, payload, { withCredentials: true });
      setInitial(JSON.stringify(data));
      toast.success("About tersimpan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Simpan gagal");
    } finally {
      setSaving(false);
    }
  };

  const openPicker = (kind, refId) => setPicker({ kind, refId });
  const handlePickMedia = (url) => {
    if (!picker) return;
    const absolute = url.startsWith("http") ? url : `${BACKEND}${url}`;
    if (picker.kind === "hero") updateField("hero.media_url", absolute);
    if (picker.kind === "story") updateSection(picker.refId, "image_url", absolute);
    if (picker.kind === "team") updateMember(picker.refId, "image_url", absolute);
  };

  if (!data) return <Loader2 className="animate-spin text-seikat-pink mx-auto mt-20" size={28} />;

  return (
    <div data-testid="admin-about">
      {/* Header */}
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="w-8 h-px bg-seikat-pink" />
            <span className="text-[10px] tracking-[0.35em] uppercase text-seikat-pink font-body">About CMS</span>
          </div>
          <h1 className="font-heading text-4xl text-[#1C1A19] leading-tight">About <em className="text-seikat-pink">Page</em></h1>
          <p className="font-body text-sm text-[#4A453F]/75 mt-1.5">Hero, story sections (drag-reorder), quote, dan tim.</p>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full text-[11px] font-body text-amber-700"><AlertCircle size={11} /> Belum disimpan</span>}
          <a href="/about" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-4 py-2 border border-[#D4859B]/20 rounded-full text-xs font-body text-[#4A453F] hover:bg-[#FDF8F6]"><ExternalLink size={11} /> Preview</a>
          <button onClick={save} disabled={saving || !isDirty} className="flex items-center gap-2 px-5 py-2.5 bg-seikat-pink text-white rounded-full text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-40" data-testid="admin-about-save">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Layers size={13} className="text-seikat-pink" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A453F] font-body">Hero</span>
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <div><label className={LABEL}>Eyebrow</label><input className={FIELD} value={data.hero?.eyebrow || ""} onChange={e => updateField("hero.eyebrow", e.target.value)} /></div>
          <div className="lg:col-span-1">
            <label className={LABEL}>Media URL (hero image)</label>
            <div className="flex gap-2">
              <input className={`${FIELD} flex-1`} value={data.hero?.media_url || ""} onChange={e => updateField("hero.media_url", e.target.value)} />
              <button type="button" onClick={() => openPicker("hero")} className="flex items-center gap-1.5 px-3 py-2 bg-seikat-pink/10 hover:bg-seikat-pink/20 text-seikat-pink rounded-xl text-xs font-body whitespace-nowrap" data-testid="hero-picker">
                <FolderOpen size={11} /> Galeri
              </button>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5"><label className={LABEL + " !mb-0"}>🇮🇩 Headline — Indonesia</label><button type="button" onClick={() => updateField("hero.headline_id", insertEm(data.hero?.headline_id || "", heroIdRef))} className="text-[10px] text-seikat-pink hover:underline flex items-center gap-1"><Italic size={9} /> +italic</button></div>
            <textarea ref={heroIdRef} rows={2} className={FIELD} value={data.hero?.headline_id || ""} onChange={e => updateField("hero.headline_id", e.target.value)} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5"><label className={LABEL + " !mb-0"}>🇬🇧 Headline — English</label><button type="button" onClick={() => updateField("hero.headline_en", insertEm(data.hero?.headline_en || "", heroEnRef))} className="text-[10px] text-seikat-pink hover:underline flex items-center gap-1"><Italic size={9} /> +italic</button></div>
            <textarea ref={heroEnRef} rows={2} className={FIELD} value={data.hero?.headline_en || ""} onChange={e => updateField("hero.headline_en", e.target.value)} />
          </div>
        </div>
        {data.hero?.media_url && <img src={data.hero.media_url} alt="" className="mt-3 w-full max-h-56 object-cover rounded-lg" />}
      </div>

      {/* Story Sections */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ImageIcon size={13} className="text-seikat-pink" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A453F] font-body">Story Sections · {data.sections?.length || 0}</span>
          </div>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
          <SortableContext items={data.sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {data.sections.map((s, idx) => (
                <SortableStory
                  key={s.id}
                  section={s}
                  idx={idx}
                  expanded={expanded.has(s.id)}
                  onToggleExpand={toggleExpand}
                  onUpdate={updateSection}
                  onRemove={removeSection}
                  onPickImage={(id) => openPicker("story", id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <button onClick={addSection} className="mt-3 w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-[#D4859B]/25 hover:border-seikat-pink rounded-2xl text-sm font-body text-[#4A453F] hover:text-seikat-pink hover:bg-seikat-pink/5 transition-all" data-testid="add-story">
          <Plus size={13} /> Tambah Story Section
        </button>
      </div>

      {/* Quote */}
      <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Quote size={13} className="text-seikat-pink" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A453F] font-body">Editorial Quote</span>
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1.5"><label className={LABEL + " !mb-0"}>🇮🇩 Quote — ID</label><button type="button" onClick={() => updateField("quote.text_id", insertEm(data.quote?.text_id || "", quoteIdRef))} className="text-[10px] text-seikat-pink hover:underline flex items-center gap-1"><Italic size={9} /> +italic</button></div>
            <textarea ref={quoteIdRef} rows={2} className={FIELD} value={data.quote?.text_id || ""} onChange={e => updateField("quote.text_id", e.target.value)} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5"><label className={LABEL + " !mb-0"}>🇬🇧 Quote — EN</label><button type="button" onClick={() => updateField("quote.text_en", insertEm(data.quote?.text_en || "", quoteEnRef))} className="text-[10px] text-seikat-pink hover:underline flex items-center gap-1"><Italic size={9} /> +italic</button></div>
            <textarea ref={quoteEnRef} rows={2} className={FIELD} value={data.quote?.text_en || ""} onChange={e => updateField("quote.text_en", e.target.value)} />
          </div>
          <div className="lg:col-span-2"><label className={LABEL}>Attribution</label><input className={FIELD} value={data.quote?.attribution || ""} onChange={e => updateField("quote.attribution", e.target.value)} placeholder="— Seikat Atelier" /></div>
        </div>
      </div>

      {/* Team */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users size={13} className="text-seikat-pink" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A453F] font-body">Tim · {data.team?.length || 0}</span>
          </div>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTeamDragEnd}>
          <SortableContext items={data.team.map(m => m._id)} strategy={verticalListSortingStrategy}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.team.map((m, idx) => (
                <SortableTeam
                  key={m._id}
                  member={m}
                  idx={idx}
                  onUpdate={updateMember}
                  onRemove={removeMember}
                  onPickImage={(id) => openPicker("team", id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <button onClick={addMember} className="mt-3 w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-[#D4859B]/25 hover:border-seikat-pink rounded-2xl text-sm font-body text-[#4A453F] hover:text-seikat-pink hover:bg-seikat-pink/5 transition-all" data-testid="add-member">
          <Plus size={13} /> Tambah Anggota Tim
        </button>
      </div>

      <MediaPickerModal open={!!picker} onClose={() => setPicker(null)} onSelect={handlePickMedia} accept="image" />
    </div>
  );
}
